import React from 'react';
import styled from 'styled-components';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { formatSaldo } from '../../utils/formatters';
import { theme } from '../../config/theme';

ChartJS.register(ArcElement, Tooltip, Legend);

const Card = styled.div`
  background-color: ${props => props.theme.colors.surface};
  padding: 1.5rem;
  border-radius: ${props => props.theme.borderRadius};
  box-shadow: ${props => props.theme.shadows.soft};
  min-width: 0;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
`;
const CardTitle = styled.h3`
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.theme.colors.textSecondary};
`;
const Content = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  flex-wrap: wrap;
`;
const DonutBox = styled.div`
  width: 170px;
  flex: 0 0 auto;
`;
const LegendList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  font-size: 0.85rem;
  min-width: 0;
`;
const LegendRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  &:last-child {
    border-top: 1px solid ${props => props.theme.colors.border};
    padding-top: 0.6rem;
    font-weight: 700;
  }
`;
const LegendLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
`;
const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.color};
  flex: 0 0 auto;
`;
const Amount = styled.span`
  font-weight: 700;
  color: ${props => props.color};
  white-space: nowrap;
`;
const EmptyText = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const SOLID_GREEN = theme.colors.success.replace('0.15', '1');

const ExposureCard = ({ moneda, totalAcreedor, totalDeudor }) => {
  const format = (value) => formatSaldo(value, moneda);
  const neto = totalAcreedor + totalDeudor;
  const sinSaldos = totalAcreedor === 0 && totalDeudor === 0;

  const chartData = {
    labels: ['Acreedores', 'Deudores'],
    datasets: [{
      data: [totalAcreedor, Math.abs(totalDeudor)],
      backgroundColor: [SOLID_GREEN, theme.colors.errorText],
      borderColor: theme.colors.surface,
      borderWidth: 2,
    }],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (c) => `${c.label}: ${format(c.parsed)}` } },
      datalabels: { display: false },
    },
  };

  return (
    <Card>
      <CardTitle>Exposición actual (saldos reales)</CardTitle>
      {sinSaldos ? (
        <EmptyText>Sin saldos para mostrar.</EmptyText>
      ) : (
        <Content>
          <DonutBox>
            <Doughnut data={chartData} options={chartOptions} />
          </DonutBox>
          <LegendList>
            <LegendRow>
              <LegendLabel><Dot color={SOLID_GREEN} /> Acreedores</LegendLabel>
              <Amount color={SOLID_GREEN}>{format(totalAcreedor)}</Amount>
            </LegendRow>
            <LegendRow>
              <LegendLabel><Dot color={theme.colors.errorText} /> Deudores</LegendLabel>
              <Amount color={theme.colors.errorText}>{format(totalDeudor)}</Amount>
            </LegendRow>
            <LegendRow>
              <LegendLabel>Neto</LegendLabel>
              <Amount color={theme.colors.secondary}>{format(neto)}</Amount>
            </LegendRow>
          </LegendList>
        </Content>
      )}
    </Card>
  );
};

export default ExposureCard;
